<template>
    <a-card :bordered="false" class="bl-card" :style="selectedOfferKeys.length && { marginBottom: '60px' }">
        <div class="bl-filterbar">
            <a-input-group compact class="fl-search-group">
                <a-select
                    v-model="searchField"
                    class="fl-search-group-field"
                    allow-clear
                    placeholder="Buscar por"
                    :dropdown-style="{ minWidth: '160px' }"
                >
                    <a-select-option v-for="option in searchFieldOptions" :key="option.key" :value="option.key">
                        {{ option.label }}
                    </a-select-option>
                </a-select>
                <a-input
                    v-model="search"
                    placeholder="Buscar"
                    allow-clear
                    class="fl-search-group-input"
                    :disabled="!searchField"
                />
            </a-input-group>

            <a-select
                class="fl-filter-select fl-filter-select-loja fixed-height-select"
                placeholder="Todas as lojas"
                v-model="storeFilter"
                mode="multiple"
                allow-clear
                show-search
                option-filter-prop="children"
                :dropdown-style="{ minWidth: '300px' }"
            >
                <a-select-option v-for="store in stores" :key="store.id" :value="store.id">
                    {{ store.name }}
                </a-select-option>
            </a-select>

            <a-select
                class="fl-filter-select fl-filter-select-fornecedor fixed-height-select"
                placeholder="Todos os fornecedores"
                v-model="providerFilter"
                mode="multiple"
                allow-clear
                show-search
                option-filter-prop="children"
                :dropdown-style="{ minWidth: '500px' }"
            >
                <a-select-option v-for="provider in providerOptions" :key="provider.key" :value="provider.key">
                    {{ provider.label }}
                </a-select-option>
            </a-select>

            <a-select
                class="fl-filter-select fixed-height-select"
                placeholder="Reabastecimento"
                v-model="replenishmentFilter"
                mode="multiple"
                allow-clear
            >
                <a-select-option v-for="option in replenishmentOptions" :key="option.key" :value="option.key">
                    {{ option.label }}
                </a-select-option>
            </a-select>
        </div>

        <a-table
            size="small"
            :columns="columns"
            :data-source="filteredRows"
            row-key="key"
            :loading="products_loading"
            :pagination="pagination"
            :scroll="{ x: 'max-content' }"
            :row-selection="rowSelection"
            :locale="{ emptyText: 'Nenhum pré-pedido pendente.' }"
        >
            <template slot="produto" slot-scope="text, row">
                <div class="bl-sup">
                    <span class="bl-n">
                        {{ row.name }}
                        <span class="brand" v-if="row.brand">{{ row.brand }}</span>
                    </span>
                    <span class="bl-v" v-if="row.ean || row.internal_code">
                        <template v-if="row.ean">EAN {{ row.ean }}</template>
                        <template v-if="row.ean && row.internal_code"> · </template>
                        <template v-if="row.internal_code">Cód. {{ row.internal_code }}</template>
                    </span>
                </div>
            </template>

            <template slot="fornecedor" slot-scope="text, row">
                <div class="bl-sup">
                    <span class="bl-n">{{ row.provider_name }}</span>
                    <span class="bl-v" v-if="row.seller_name">{{ row.seller_name }}</span>
                </div>
            </template>

            <template slot="loja" slot-scope="text, row">
                <div class="bl-strong">{{ storeName(row.store_id) }}</div>
            </template>

            <template slot="ressuprimento" slot-scope="text, row">
                <span class="repl-tag" v-if="replenishment(row)">{{ replenishment(row) }}</span>
                <span v-else>--</span>
            </template>

            <template slot="estoque" slot-scope="text, row">
                <span v-if="row.inventory != null">{{ row.inventory | stock }}</span>
                <span v-else>—</span>
            </template>

            <template slot="embalagem" slot-scope="text, row">
                {{
                    row.purchase_unity ||
                    `${row.packing.split(" ")[0]} ${row.packing.split(" ")[1]}`
                }}
            </template>

            <template slot="requisitada" slot-scope="text, row">
                <span v-if="row.request_quantity != null">{{ row.request_quantity }}</span>
                <span v-else>—</span>
            </template>

            <template slot="quantidade" slot-scope="text, row">
                <span class="matrix-qty-wrap" :class="{ saved: !!savedCells[`${row.id}-${row.store_id}-${row.provider_id}-${row.seller_id}`] }">
                    <a-input
                        :min="0"
                        data-field="flat-qty"
                        class="matrix-qty-input"
                        v-model="row.requested_quantity"
                        @input="handleQuantityInput($event, row)"
                        @blur="saveQuantity(row)"
                        @keydown.native="handleQtyKeydown($event)"
                        @focus="handleFocus(row)"
                    />
                </span>
            </template>

            <template slot="total_quantity" slot-scope="text, row">{{ totalQuantity(row) }}</template>

            <template slot="offer_price" slot-scope="text, row">{{ row.offer_price | currency }}</template>

            <template slot="packing_value" slot-scope="text, row">{{ packageValue(row) | currency }}</template>

            <template slot="last_purchase" slot-scope="text, row">
                <span v-if="row.last_purchase != null">{{ row.last_purchase | currency }}</span>
                <span v-else>—</span>
            </template>

            <template slot="diferenca" slot-scope="text, row">
                <span v-if="row.last_purchase != null" :class="deltaClass(row)">
                    {{ (row.offer_price - row.last_purchase) | currency }}
                </span>
                <span v-else>—</span>
            </template>

            <template slot="variacao" slot-scope="text, row">
                <span v-if="row.last_purchase != null" :class="deltaClass(row)">
                    {{ variation(row).toFixed(1) }}%
                </span>
                <span v-else>—</span>
            </template>

            <template slot="total" slot-scope="text, row">{{ rowTotal(row) | currency }}</template>

            <template slot="acoes" slot-scope="text, row">
                <ActionMenu :actions="rowActions(row)" />
            </template>
        </a-table>

        <NegotiationModal
            v-if="negotiationModal.open"
            :product="negotiationModal.product"
            :typing="negotiationModal.winner"
            :quotation_id="$route.params.id"
            @close="closeNegotiation"
        />

        <ProductWrapModal
            v-if="wrapModal.open"
            :product="wrapModal.product"
            :salesman="wrapModal.winner"
            @close="closeWrap"
        />

        <ChangeWinnerModal
            v-if="changeWinnerModal.open"
            :product="changeWinnerModal.product"
            :pre-orders="products"
            :quotation-id="$route.params.id"
            :store-id="changeWinnerModal.product.store_id"
            :store-name="storeName(changeWinnerModal.product.store_id)"
            @close="closeChangeWinner"
            @saved="onWinnerChanged"
        />

        <ProductOrdersModal
            v-if="historyModal.open"
            :product_id="historyModal.productId"
            :product_name="historyModal.productName"
            @close="historyModal.open = false"
        />

        <SelectionFooter
            :count="selectedOffers.length"
            item-label="ofertas selecionadas"
        >
            <template slot="summary">
                <strong>{{ selectedTotal | currency }}</strong>
            </template>

            <template slot="actions">
                <a-button size="large" @click="$emit('update:selectedOfferKeys', [])">Limpar seleção</a-button>
                <a-button size="large" @click="bulkExport">Exportar</a-button>
                <a-button size="large" @click="bulkZeroQuantity">Zerar quantidade</a-button>
                <a-button size="large" :loading="refreshingErp" :disabled="refreshingErp || generating" @click="$emit('refresh-erp', selectedOfferKeys)">Atualizar estoque/custo</a-button>
                <a-button size="large" type="primary" :disabled="generating" @click="bulkGenerateOrder">Gerar pedido</a-button>
            </template>
        </SelectionFooter>
    </a-card>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import currencyPrecision from "@/mixins/currencyPrecision";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { replenishmentLabel } from "@/helpers/replenishment";
import { replenishmentStrategyMixin } from "@/mixins/replenishment-strategy.mixin";
import NegotiationModal from "@/modals/negotiate-offer/negotiate-offer.modal.vue";
import ProductWrapModal from "@/modals/product-wrap/product-wrap.modal.vue";
import ChangeWinnerModal from "@/modals/change-winner/change-winner.modal.vue";
import ProductOrdersModal from "@/modals/products/orders";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";

const PRICE_LOCKED_TOOLTIP = "Pedido já gerado com este fornecedor — preço travado";

export default {
    name: "FlatTable",
    mixins: [currencyPrecision, loaderMixin, replenishmentStrategyMixin],
    components: { NegotiationModal, ProductWrapModal, ChangeWinnerModal, ProductOrdersModal, ActionMenu, SelectionFooter },
    props: {
        // Fetched once by the parent (_pre-orders.tab.vue) and shared with
        // Matriz/Por Produto/Por loja (nova API) — same pre_orders_report()
        // call for all four, see [[winners-v2-backlog-triage]].
        products: {
            type: Array,
            default: () => [],
        },
        products_loading: {
            type: Boolean,
            default: false,
        },
        orderedOffers: {
            type: Object,
            default: () => ({}),
        },
        // Another generate-orders call (from this view or a sibling one) is
        // still in flight — blocks new ones so an early reload can't run
        // while a later request is still writing [[pre-orders-generate-race]].
        generating: {
            type: Boolean,
            default: false,
        },
        refreshingErp: {
            type: Boolean,
            default: false,
        },
        pendencyOnly: {
            type: Boolean,
            default: false,
        },
        // Shared with Por Loja / Por Produto via `.sync` on the parent
        // (_pre-orders.tab.vue) — same row-key shape in all three.
        selectedOfferKeys: {
            type: Array,
            default: () => [],
        },
        // Same reasoning as selectedOfferKeys: owned by the parent and
        // shared across the 4 views so a "saved" marker survives switching
        // tabs, keyed the same way (`${produto}-${loja}-${fornecedor}-${vendedor}`).
        savedCells: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            stores: [],
            // Derived from the `products` prop with a stable composite key
            // added — see the `products` watcher below.
            keyedProducts: [],
            svc: new QuotationService(),
            search: "",
            searchField: undefined,
            searchFieldOptions: [
                { key: "name", label: "Produto" },
                { key: "ean", label: "EAN" },
                { key: "internal_code", label: "Código" },
            ],
            storeFilter: [],
            providerFilter: [],
            replenishmentFilter: [],
            pagination: {
                pageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                locale: {
                    items_per_page: '/ página',
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            negotiationModal: {
                open: false,
                winner: null,
                product: null,
            },
            wrapModal: {
                open: false,
                winner: null,
                product: null,
            },
            changeWinnerModal: {
                open: false,
                product: null,
            },
            historyModal: {
                open: false,
                productId: null,
                productName: "",
            },
        };
    },
    computed: {
        columns() {
            return [
                // Identidade da linha
                { title: "Produto", key: "produto", width: 210, scopedSlots: { customRender: "produto" } },
                { title: "Fornecedor", key: "fornecedor", width: 160, scopedSlots: { customRender: "fornecedor" } },
                { title: "Loja", key: "loja", width: 100, scopedSlots: { customRender: "loja" } },

                // Atributos descritivos
                { title: "Ressup.", key: "ressuprimento", width: 90, align: "center", scopedSlots: { customRender: "ressuprimento" } },
                { title: "Estoque", key: "estoque", width: 60, align: "center", scopedSlots: { customRender: "estoque" } },
                { title: "Emb", key: "embalagem", width: 55, align: "center", scopedSlots: { customRender: "embalagem" } },

                // Quantidades
                { title: "Qtd. requisitada", key: "requisitada", width: 50, align: "center", scopedSlots: { customRender: "requisitada" } },
                { title: "Quantidade", key: "quantidade", width: 90, align: "center", scopedSlots: { customRender: "quantidade" } },
                { title: "Qtd. total", key: "total_quantity", width: 50, align: "center", scopedSlots: { customRender: "total_quantity" } },

                // Valores
                { title: "Valor", key: "offer_price", width: 75, align: "center", scopedSlots: { customRender: "offer_price" } },
                { title: "Valor emb.", key: "packing_value", width: 85, align: "center", scopedSlots: { customRender: "packing_value" } },
                { title: "Últ. Compra", key: "last_purchase", width: 75, align: "center", scopedSlots: { customRender: "last_purchase" } },
                { title: "Diferença", key: "diferenca", width: 85, align: "center", scopedSlots: { customRender: "diferenca" } },
                { title: "Variação", key: "variacao", width: 80, align: "center", scopedSlots: { customRender: "variacao" } },
                { title: "Total", key: "total", width: 95, align: "center", scopedSlots: { customRender: "total" } },

                { title: "Ações", key: "acoes", width: 60, fixed: "right", scopedSlots: { customRender: "acoes" } },
            ];
        },
        providerOptions() {
            const map = {};

            this.keyedProducts.forEach((row) => {
                const key = `${row.provider_id}-${row.seller_id}`;

                if (!map[key]) {
                    map[key] = {
                        key,
                        label: row.seller_name ? `${row.provider_name} · ${row.seller_name}` : row.provider_name,
                    };
                }
            });

            return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
        },
        replenishmentOptions() {
            const seen = new Set();
            const options = [];

            this.keyedProducts.forEach((row) => {
                const key = row.replenishment_strategy;

                if (!key || seen.has(key)) return;

                seen.add(key);
                options.push({ key, label: replenishmentLabel(key) });
            });

            return options.sort((a, b) => a.label.localeCompare(b.label));
        },
        belowMinimumKeys() {
            const totals = {};

            this.keyedProducts.forEach((row) => {
                const key = `${row.store_id}-${row.provider_id}-${row.seller_id}`;
                totals[key] = (totals[key] || 0) + this.rowTotal(row);
            });

            const below = new Set();

            this.keyedProducts.forEach((row) => {
                const key = `${row.store_id}-${row.provider_id}-${row.seller_id}`;

                if (row.minimum_billing && totals[key] < row.minimum_billing) {
                    below.add(key);
                }
            });

            return below;
        },
        filteredRows() {
            const term = (this.search || "").trim().toLowerCase();

            return this.keyedProducts.filter((row) => {
                if (this.storeFilter.length && !this.storeFilter.some(id => row.store_id == id)) return false;

                if (this.providerFilter.length && !this.providerFilter.includes(`${row.provider_id}-${row.seller_id}`)) return false;

                if (this.replenishmentFilter.length && !this.replenishmentFilter.includes(row.replenishment_strategy)) return false;

                if (this.searchField && term) {
                    const value = String(row[this.searchField] ?? "").toLowerCase();
                    if (!value.includes(term)) return false;
                }

                if (this.pendencyOnly && !this.belowMinimumKeys.has(`${row.store_id}-${row.provider_id}-${row.seller_id}`)) return false;

                return true;
            });
        },
        distinctProductSiblings() {
            const seen = new Set();
            const list = [];

            this.filteredRows.forEach((row) => {
                if (!seen.has(row.id)) {
                    seen.add(row.id);
                    list.push(row);
                }
            });

            return list;
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedOfferKeys,
                onChange: (selectedRowKeys) => {
                    this.$emit('update:selectedOfferKeys', selectedRowKeys);
                },
                onSelectAll: async (selected) => {
                    if (this.filteredRows.length <= this.pagination.pageSize) return;

                    const filteredKeys = this.filteredRows.map((row) => row.key);

                    if (selected) {
                        const result = await this.$swal({
                            title: 'Selecionar ofertas',
                            text: 'Deseja selecionar apenas a página atual ou todas as ofertas filtradas?',
                            icon: 'question',
                            showCancelButton: true,
                            confirmButtonText: 'Todas',
                            cancelButtonText: 'Página atual',
                        });

                        if (result.isConfirmed) {
                            this.$emit('update:selectedOfferKeys', Array.from(new Set([...this.selectedOfferKeys, ...filteredKeys])));
                        }

                        return;
                    }

                    const result = await this.$swal({
                        title: 'Remover seleção',
                        text: 'Deseja remover apenas a página atual ou todas as ofertas filtradas?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Todas',
                        cancelButtonText: 'Página atual',
                    });

                    if (result.isConfirmed) {
                        const filteredSet = new Set(filteredKeys);
                        this.$emit('update:selectedOfferKeys', this.selectedOfferKeys.filter((key) => !filteredSet.has(key)));
                    }
                },
            };
        },
        selectedOffers() {
            const keys = new Set(this.selectedOfferKeys);
            return this.keyedProducts.filter((row) => keys.has(row.key));
        },
        selectedTotal() {
            return this.selectedOffers.reduce((sum, row) => sum + this.rowTotal(row), 0);
        },
    },
    methods: {
        isPriceLocked(productId, sellerId) {
            return !!this.orderedOffers[`${productId}-${sellerId}`];
        },
        replenishment(row) {
            return replenishmentLabel(row.replenishment_strategy)
        },
        storeName(storeId) {
            const store = this.stores.find(s => s.id == storeId)
            return store ? store.name : "—"
        },
        packingUnits(row) {
            return row.is_generic == 0 && row.main_product_id == null
                ? parseInt(row.packing.split(' ')[1]) || 1
                : 1
        },
        packageValue(row) {
            return this.packingUnits(row) * row.offer_price
        },
        totalQuantity(row) {
            return (row.requested_quantity || 0) * this.packingUnits(row)
        },
        rowTotal(row) {
            return this.totalQuantity(row) * row.offer_price
        },
        deltaClass(row) {
            if (row.offer_price > row.last_purchase) return "vpm-up"
            if (row.offer_price < row.last_purchase) return "vpm-down"
            return ""
        },
        variation(row) {
            if (!row.last_purchase) return 0
            return ((row.offer_price - row.last_purchase) / row.last_purchase) * 100
        },
        handleFocus(row) {
            const column = this.stores.find(s => s.id == row.store_id)

            this.$emit('cell-focus', row, column ? column.position : row.store_id, this.distinctProductSiblings)
        },
        saveQuantity(row) {
            this.$emit('save-quantity', {
                key: row.key,
                product_id: row.id,
                store_id: row.store_id,
                seller_id: row.seller_id,
            })
        },
        handleQuantityInput(event, row) {
            let value = event.target.value.replace(/\D/g, '')

            value = value.replace(/^0+(?=\d)/, '')

            if (+value > 999999) {
                value = '999999'
            }

            row.requested_quantity = value
            this.$emit('quantity-edited', { key: row.key, requested_quantity: value });

            event.target.value = value
        },
        handleQtyKeydown(event) {
            if (
                !['Enter', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)
            ) {
                return
            }

            event.preventDefault()

            const inputs = Array.from(
                document.querySelectorAll('input.ant-input')
            ).filter(
                el => el.dataset.field === 'flat-qty'
            )

            const currentIndex = inputs.indexOf(event.target)

            if (currentIndex === -1) {
                return
            }

            const forward = ['Enter', 'ArrowDown', 'ArrowRight'].includes(event.key)

            const nextIndex = Math.max(
                0,
                Math.min(currentIndex + (forward ? 1 : -1), inputs.length - 1)
            )

            const nextInput = inputs[nextIndex]

            if (nextInput) {
                nextInput.focus()
                nextInput.select()
            }
        },
        bulkGenerateOrder() {
            if (!this.selectedOffers.length) return;

            this.confirm_action({
                message: `Gerar pedido para ${this.selectedOffers.length} oferta(s) selecionada(s)?`,
                confirm_text: "Gerar pedidos",
                callback: () => {
                    this.$emit('generate-orders', this.selectedOfferKeys);
                },
            });
        },
        bulkZeroQuantity() {
            if (!this.selectedOffers.length) return;

            this.confirm_action({
                message: `Zerar a quantidade de ${this.selectedOffers.length} oferta(s) selecionada(s)?`,
                confirm_text: "Zerar quantidade",
                callback: () => {
                    const keys = this.selectedOffers.map((row) => row.key);

                    Promise.all(
                        this.selectedOffers.map((row) => {
                            row.requested_quantity = 0;

                            return this.svc.sendQuantidade({
                                numerocotacao: this.$route.params.id,
                                quantidade: 0,
                                vendedor: row.seller_id,
                                produto: row.id,
                                loja: row.store_id,
                            });
                        })
                    )
                        .then(() => {
                            keys.forEach((key) => this.$emit('quantity-synced', { key, requested_quantity: 0 }));
                            this.snack("Quantidades zeradas com sucesso.");
                            this.$emit('update:selectedOfferKeys', []);
                        })
                        .catch((error) => {
                            this.snack("Não foi possível zerar uma ou mais quantidades.", "error");
                            ErrorHandlerService.handle(error, this.$store);
                        });
                },
            });
        },
        bulkExport() {
            if (!this.selectedOffers.length) return;

            const headers = ["Produto", "Marca", "Fornecedor", "Vendedor", "Loja", "Valor", "Quantidade", "Total"];

            const rows = this.selectedOffers.map((row) => [
                row.name,
                row.brand || "",
                row.provider_name,
                row.seller_name || "",
                this.storeName(row.store_id),
                row.offer_price,
                row.requested_quantity || 0,
                this.rowTotal(row),
            ]);

            const csv = [headers, ...rows]
                .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
                .join("\n");

            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = `pre-pedidos-analitico-${this.$route.params.id}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            this.$emit('update:selectedOfferKeys', []);
        },
        rowActions(row) {
            const priceLocked = this.isPriceLocked(row.id, row.seller_id);

            return [
                { label: "Análise de giro", icon: "query_stats", iconClass: "action-info", onClick: () => this.$emit("giro", row, this.distinctProductSiblings) },
                { label: "Negociar preço", icon: "handshake", iconClass: "action-success", disabled: priceLocked, tooltip: priceLocked ? PRICE_LOCKED_TOOLTIP : null, onClick: () => this.openNegotiation(row) },
                { label: "Alterar embalagem", icon: "inventory_2", iconClass: "action-warning", onClick: () => this.openWrap(row) },
                { label: "Alterar vencedor", icon: "emoji_events", iconClass: "action-teal", onClick: () => this.openChangeWinner(row) },
                { label: "Histórico de pedidos", icon: "assignment", iconClass: "action-teal", onClick: () => this.openHistory(row) },
                { label: "Alterar tipo de reabastecimento", icon: "sync_alt", iconClass: "action-primary", disabled: this.savingReplenishment === row.id, onClick: () => this.promptReplenishmentStrategy(row) },
            ];
        },
        openNegotiation(record) {
            this.negotiationModal = {
                open: true,
                winner: {
                    idVendedor: record.seller_id,
                    idFornecedor: record.provider_id,
                    pro_id: record.id,
                    valor: record.offer_price,
                    nomeVendedor: record.seller_name,
                },
                product: {
                    produtoDescricao: record.name,
                    pro_observacao: record.brand,
                    observacao: record.observations,
                    ultimaCompra: record.last_purchase,
                },
            };
        },
        closeNegotiation() {
            this.negotiationModal.open = false;
            this.$emit('reload');
        },
        openWrap(record) {
            this.wrapModal = {
                open: true,
                winner: {
                    idVendedor: record.seller_id,
                    idFornecedor: record.provider_id,
                    pro_id: record.id,
                    embalagem: record.packing,
                },
                product: {
                    descricaoProduto: record.name,
                    embalagem: record.packing,
                    idProduto: record.id,
                    flag_generico: record.is_generic,
                    pro_principal: record.main_product_id,
                },
            };
        },
        closeWrap() {
            this.wrapModal.open = false;
            this.$emit('reload');
        },
        openChangeWinner(record) {
            this.changeWinnerModal = {
                open: true,
                product: record,
            };
        },
        closeChangeWinner() {
            this.changeWinnerModal.open = false;
        },
        onWinnerChanged() {
            this.$emit('reload');
        },
        openHistory(row) {
            this.historyModal = {
                open: true,
                productId: Number(row.id),
                productName: row.name,
            };
        },
        keyProducts() {
            this.keyedProducts = this.products.map(row => ({
                ...row,
                key: `${row.id}-${row.store_id}-${row.provider_id}-${row.seller_id}`,
            }));
        },
        sort_stores() {
            this.stores.sort((a, b) => {
                if (a.position > b.position) return 1
                if (b.position > a.position) return -1
                return 0
            })
        },
        async loadStores() {
            this.stores = await this.$store.dispatch('user/fetch_stores')
            this.sort_stores()
        },
    },
    created() {
        this.loadStores();
    },
    watch: {
        searchField() {
            this.search = "";
        },
        products: {
            immediate: true,
            handler(value) {
                if (!value || !value.length) return;
                this.keyProducts();
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.bl-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
    ::v-deep(.ant-table table) {
        table-layout: fixed;
    }
}
.bl-filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
}
.fl-filter-select {
    width: 220px;
}
.fl-filter-select-loja {
    width: 260px;
}
.fl-filter-select-fornecedor {
    width: 320px;
}
.fixed-height-select.ant-select,
.fixed-height-select ::v-deep(.ant-select-selection--multiple) {
    min-height: 32px !important;
    max-height: 32px !important;
    overflow: auto;
}
.fl-search-group {
    display: flex !important;
    flex: 1 1 240px;
    min-width: 220px;
    max-width: 340px;
}
.fl-search-group-field {
    width: 130px;
    flex: 0 0 auto;
}
.fl-search-group-input {
    flex: 1;
    min-width: 0;
}
.fl-search-group ::v-deep(.ant-select-selector),
.fl-search-group ::v-deep(.ant-input) {
    height: 32px !important;
}
.bl-strong {
    font-weight: 600;
    color: #262626;
}
.bl-sup {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.bl-n {
    font-weight: 600;
    color: #262626;
}
.bl-v {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}
.repl-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.brand {
    margin-left: 6px;
    color: #999;
    font-size: 12px;
    font-weight: 400;
}
.vpm-up {
    color: #cf1322;
    font-weight: 600;
}
.vpm-down {
    color: #389e0d;
    font-weight: 600;
}
.matrix-qty-input {
    width: 70px;
    text-align: center;

    .ant-input {
        padding: 2px 4px;
    }
}
.matrix-qty-wrap {
    display: contents;
}
.matrix-qty-wrap.saved ::v-deep(.ant-input) {
    background-color: #13c7213d;
    color: green !important;
}
</style>
